<template>
  <div>
    <!-- Hero Section -->
    <section class="contact-hero-section">
      <div class="hero-bg-elements">
        <div class="hero-bg-shape-1"></div>
        <div class="hero-bg-shape-2"></div>
        <div class="hero-bg-dots"></div>
      </div>
      <div class="container position-relative">
        <div class="row min-vh-50 align-items-center py-5">
          <div class="col-lg-8 mx-auto text-center">
            <div class="section-badge mb-2" data-aos="fade-up">
              <span class="badge-icon"><i class="bi bi-envelope"></i></span>
              <span class="badge-text">GET IN TOUCH</span>
            </div>
            <h1 class="section-title mb-3" data-aos="fade-up" data-aos-delay="100">Let's <span class="text-gradient">Connect</span></h1>
            <div class="section-line mx-auto" data-aos="fade-up" data-aos-delay="150"></div>
            <p class="section-subtitle" data-aos="fade-up" data-aos-delay="200">
              Have questions about intellectual property strategy for your biotech innovation? I'm here to help.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Form Section -->
    <section class="contact-form-section py-6">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="contact-form-wrapper" data-aos="fade-up">
              <div class="row">
                <div class="col-lg-12 text-center mb-4">
                  <h2 class="section-title mb-3">Send a <span class="text-gradient">Message</span></h2>
                  <p class="section-subtitle">I'll respond to your inquiry as soon as possible</p>
                </div>
              </div>
              
              <form @submit.prevent="submitForm" class="contact-form p-4 p-md-5">
                <div class="row g-4">
                  <div class="col-md-6">
                    <div class="form-floating mb-3">
                      <input
                        type="text"
                        class="form-control"
                        :class="{'is-invalid': formTouched && !form.firstName.trim()}"
                        id="firstName"
                        placeholder="First Name"
                        v-model="form.firstName"
                        @blur="formTouched = true"
                      />
                      <label for="firstName">First Name</label>
                      <div class="invalid-feedback">First name is required</div>
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <div class="form-floating mb-3">
                      <input
                        type="text"
                        class="form-control"
                        :class="{'is-invalid': formTouched && !form.lastName.trim()}"
                        id="lastName"
                        placeholder="Last Name"
                        v-model="form.lastName"
                        @blur="formTouched = true"
                      />
                      <label for="lastName">Last Name</label>
                      <div class="invalid-feedback">Last name is required</div>
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <div class="form-floating mb-3">
                      <input
                        type="email"
                        class="form-control"
                        :class="{'is-invalid': formTouched && (!form.email.trim() || !validateEmail(form.email))}"
                        id="email"
                        placeholder="Email Address"
                        v-model="form.email"
                        @blur="formTouched = true"
                      />
                      <label for="email">Email Address</label>
                      <div class="invalid-feedback">
                        {{ !form.email.trim() ? 'Email is required' : 'Please enter a valid email address' }}
                      </div>
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <div class="form-floating mb-3">
                      <input
                        type="tel"
                        class="form-control"
                        id="phone"
                        placeholder="Phone Number"
                        v-model="form.phone"
                      />
                      <label for="phone">Phone Number</label>
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <div class="form-floating mb-3">
                      <input
                        type="text"
                        class="form-control"
                        id="company"
                        placeholder="Company (Optional)"
                        v-model="form.company"
                      />
                      <label for="company">Company (Optional)</label>
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <div class="form-floating mb-3">
                      <select
                        id="service"
                        v-model="form.service"
                        class="form-select"
                        :class="{'is-invalid': formTouched && !form.service}"
                        aria-label="Service of Interest"
                        @blur="formTouched = true"
                      >
                        <option value=""></option>
                        <option value="patent-strategy">Patent Strategy & Portfolio Management</option>
                        <option value="ip-due-diligence">IP Due Diligence</option>
                        <option value="regulatory-compliance">Regulatory Compliance & IP Strategy</option>
                        <option value="licensing">Licensing & Collaboration Agreements</option>
                        <option value="competitive-intelligence">Competitive Intelligence</option>
                        <option value="litigation-support">IP Litigation Support</option>
                        <option value="other">Other (please specify in message)</option>
                      </select>
                      <label for="service">Service of Interest</label>
                      <div class="invalid-feedback">Please select a service</div>
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <!-- Empty div for alignment -->
                  </div>
                  
                  <div class="col-12">
                    <div class="mb-3">
                      <label for="message" class="form-label">Your Message</label>
                      <textarea
                        id="message"
                        class="form-control"
                        :class="{'is-invalid': formTouched && !form.message.trim()}"
                        rows="5"
                        v-model="form.message"
                        @blur="formTouched = true"
                      ></textarea>
                      <div class="invalid-feedback">Please enter your message</div>
                    </div>
                  </div>
                  
                  <div class="col-12 mb-3">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        :class="{'is-invalid': formTouched && !form.privacy}"
                        type="checkbox"
                        id="privacy"
                        v-model="form.privacy"
                      />
                      <label class="form-check-label" for="privacy">
                        I agree to the privacy policy and consent to being contacted regarding my inquiry.
                      </label>
                      <div class="invalid-feedback">You must agree to the privacy policy</div>
                    </div>
                  </div>
                  
                  <!-- Form Success Message -->
                  <div v-if="formSubmitted" class="mt-4 alert alert-success" role="alert">
                    <i class="bi bi-check-circle me-2"></i>
                    Thank you for your message! We'll get back to you shortly.
                  </div>

                  <!-- Form Error Message -->
                  <div v-if="formError" class="mt-4 alert alert-danger" role="alert">
                    <i class="bi bi-exclamation-triangle me-2"></i>
                    {{ errorMessage }}
                  </div>

                  <!-- Submit Button -->
                  <div v-if="!formSubmitted" class="mt-4 d-grid gap-2 d-md-flex justify-content-md-end">
                    <button 
                      type="submit" 
                      class="btn btn-primary btn-lg" 
                      style="background-color: #0d6efd; border-color: #0d6efd;"
                      :disabled="submitting || !form.privacy || !isFormValid"
                    >
                      <i v-if="submitting" class="bi bi-arrow-repeat spin me-2"></i>
                      <span v-if="submitting">Sending...</span>
                      <span v-else>Send Message</span>
                    </button>
                  </div>
                </div>

              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import emailjs from '@emailjs/browser';

export default {
  name: 'ContactPage',
  inject: ['themeState'],
  computed: {
    isDarkTheme() {
      return this.themeState();
    },
    isFormValid() {
      return (
        this.form.firstName.trim() !== '' &&
        this.form.lastName.trim() !== '' &&
        this.form.email.trim() !== '' &&
        this.validateEmail(this.form.email) &&
        this.form.service !== '' &&
        this.form.message.trim() !== ''
      );
    }
  },
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: '',
        privacy: false
      },
      formTouched: false,
      submitting: false,
      formSubmitted: false,
      formError: false,
      errorMessage: ''
    }
  },
  mounted() {
    // Initialize EmailJS with your public key
    // You'll need to replace this with your actual EmailJS public key
    emailjs.init('YOUR_PUBLIC_KEY');
  },
  methods: {
    async submitForm() {
      this.formTouched = true;
      
      if (!this.isFormValid) {
        return;
      }
      
      this.submitting = true;
      this.formError = false;
      
      try {
        // Prepare template parameters
        const templateParams = {
          from_name: `${this.form.firstName} ${this.form.lastName}`,
          from_email: this.form.email,
          phone: this.form.phone || 'Not provided',
          company: this.form.company || 'Not provided',
          service: this.getServiceLabel(this.form.service),
          message: this.form.message
        };
        
        // Send the email using EmailJS
        // Replace 'YOUR_SERVICE_ID' and 'YOUR_TEMPLATE_ID' with your actual EmailJS service and template IDs
        await emailjs.send('YOUR_SERVICE_ID', 'YOUR_TEMPLATE_ID', templateParams);
        
        // Handle successful submission
        this.formSubmitted = true;
        
        // Reset form after submission
        this.form = {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: '',
          privacy: false
        };
        this.formTouched = false;
      } catch (error) {
        // Handle error
        console.error('Email sending failed:', error);
        this.formError = true;
        this.errorMessage = 'There was an error sending your message. Please try again later.';
      } finally {
        this.submitting = false;
      }
    },
    
    getServiceLabel(serviceValue) {
      const serviceOptions = {
        'patent-strategy': 'Patent Strategy & Portfolio Management',
        'ip-due-diligence': 'IP Due Diligence',
        'regulatory-compliance': 'Regulatory Compliance & IP Strategy',
        'licensing': 'Licensing & Collaboration Agreements',
        'competitive-intelligence': 'Competitive Intelligence',
        'litigation-support': 'IP Litigation Support',
        'other': 'Other'
      };
      
      return serviceOptions[serviceValue] || 'Not selected';
    },
    
    validateEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }
  }
}
</script>

<style scoped>
/* Hero Section Styles */
.contact-hero-section {
  position: relative;
  padding: 6rem 0;
  overflow: hidden;
  background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
}

.hero-bg-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  overflow: hidden;
}

.hero-bg-shape-1 {
  position: absolute;
  top: -150px;
  right: -150px;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(var(--gradient-start-rgb), 0.1), rgba(var(--gradient-end-rgb), 0.1));
  z-index: -1;
}

.hero-bg-shape-2 {
  position: absolute;
  bottom: -100px;
  left: -100px;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(var(--gradient-end-rgb), 0.1), rgba(var(--gradient-start-rgb), 0.1));
  z-index: -1;
}

.hero-bg-dots {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px);
  background-size: 20px 20px;
  z-index: -1;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(var(--primary-rgb), 0.1);
  border-radius: 50px;
  padding: 0.5rem 1rem;
  margin-bottom: 1.5rem;
}

.badge-icon {
  margin-right: 0.5rem;
  color: var(--primary);
}

.badge-text {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 1px;
  color: var(--primary);
}

.section-title {
  color: var(--text-color);
  font-weight: 700;
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
}

.text-gradient {
  background: linear-gradient(to right, var(--gradient-start), var(--gradient-end));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline;
}

.section-line {
  width: 50px;
  height: 3px;
  background: linear-gradient(to right, var(--gradient-start), var(--gradient-end));
  margin: 0 auto 1.5rem;
}

.section-subtitle {
  color: var(--text-muted);
  font-size: 1.1rem;
  max-width: 700px;
  margin: 0 auto;
}

/* Contact Form Section Styles */
.contact-form-section {
  padding: 5rem 0;
  background-color: var(--bg-primary);
  position: relative;
}

.contact-form-wrapper {
  position: relative;
  margin-top: -3rem;
  z-index: 1;
}

.contact-form {
  background: var(--card-bg);
  border-radius: 20px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--card-border);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.contact-form::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: linear-gradient(to right, var(--gradient-start), var(--gradient-end));
}

/* Form Styles */
.form-floating > .form-control,
.form-floating > .form-select {
  height: calc(3.5rem + 2px);
  padding: 1rem 0.75rem;
  background-color: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-color);
  border-radius: 10px;
  transition: all 0.3s ease;
}

.form-floating > .form-control:focus,
.form-floating > .form-select:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 0.25rem rgba(var(--primary-rgb), 0.15);
  background-color: var(--bg-tertiary);
}

.form-floating > label {
  padding: 1rem 0.75rem;
  color: var(--text-muted);
}

.form-floating > .form-control:focus ~ label,
.form-floating > .form-control:not(:placeholder-shown) ~ label,
.form-floating > .form-select ~ label {
  color: var(--primary);
  transform: scale(0.85) translateY(-0.5rem) translateX(0.15rem);
}

.form-floating > textarea.form-control {
  height: auto;
  min-height: 150px;
}

.form-check-input {
  background-color: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  width: 1.2em;
  height: 1.2em;
  margin-top: 0.15em;
}

.form-check-input:checked {
  background-color: var(--primary);
  border-color: var(--primary);
}

.form-check-label {
  color: var(--text-muted);
  font-size: 0.9rem;
  margin-left: 0.5rem;
}

.btn-primary {
  background: linear-gradient(to right, var(--gradient-start), var(--gradient-end));
  border: none;
  color: white;
  transition: all 0.3s ease;
  border-radius: 50px;
  padding: 0.8rem 2rem;
  font-weight: 600;
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.btn-primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, var(--gradient-end), var(--gradient-start));
  opacity: 0;
  transition: all 0.3s ease;
  z-index: -1;
}

.btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(var(--primary-rgb), 0.3);
}

.btn-primary:hover::before {
  opacity: 1;
}

.btn-primary:disabled {
  background: #6c757d;
  transform: none;
}

.btn-lg {
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
}

.alert-success {
  background-color: rgba(var(--success-rgb), 0.1);
  border-color: rgba(var(--success-rgb), 0.2);
  color: var(--success);
  border-radius: 10px;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Responsive Styles */
@media (max-width: 992px) {
  .section-title {
    font-size: 2rem;
  }
  
  .contact-form {
    padding: 2rem;
  }
}

@media (max-width: 768px) {
  .contact-hero-section {
    padding: 4rem 0;
  }
  
  .contact-form-section {
    padding: 3rem 0;
  }
  
  .section-title {
    font-size: 1.8rem;
  }
  
  .contact-form {
    padding: 1.5rem;
    margin-top: 0;
  }
  
  .contact-form-wrapper {
    margin-top: 0;
  }
}
</style>
